/**
 * Proaktive KI - "Was soll ich als Naechstes tun?" (CompanyBrain Welle 2, BEN-106).
 * Hooks fuer die priorisierte, rollenbewusste Naechste-Schritte-Liste aus echten Daten.
 */
import { useQuery } from "@tanstack/react-query";
import { useApi } from "./api";
import { useTenant } from "./tenant";

export type ProactiveIntervention = {
  code: string;
  name: string;
  route: string;
  score: number;
  warum: string[];
};

export type NextActionCategory = "writeback_approval" | "kpi" | "verzug" | "wp" | "prozess" | "evidence" | "profil" | "decision" | "meeting" | "ai_ready" | "kanban" | "formular";
export type NextActionPriority = "hoch" | "mittel" | "niedrig";

export type NextAction = {
  id: string;
  category: NextActionCategory;
  priority: NextActionPriority;
  title: string;
  why: string;
  recommendation: string;
  route: string;
  action_label: string;
  roles: string[];
  intervention: ProactiveIntervention | null;
  evidence_refs: string[];
};

export type NextActionsResponse = {
  tenant_id: string;
  role: "berater" | "kunde";
  generated_at: string;
  focus_today: string;
  focus_engine: string;
  count: number;
  actions: NextAction[];
  degraded: boolean;
  _meta?: Record<string, unknown>;
};

export function useNextActions(
  role: "berater" | "kunde",
  opts?: { limit?: number; focus?: boolean; enabled?: boolean },
) {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const limit = opts?.limit ?? 8;
  const focus = opts?.focus ? 1 : 0;
  return useQuery({
    queryKey: ["next-actions", effectiveTenantId, role, limit, focus],
    queryFn: () =>
      api.get<NextActionsResponse>(
        `/proactive/next-actions?role=${role}&limit=${limit}&focus=${focus}`,
      ),
    enabled: opts?.enabled ?? Boolean(effectiveTenantId),
    retry: 1,
    staleTime: 60_000,
  });
}

export const PRIORITY_LABEL: Record<NextActionPriority, string> = {
  hoch: "Hoch",
  mittel: "Mittel",
  niedrig: "Niedrig",
};

export const CATEGORY_LABEL: Record<NextActionCategory, string> = {
  writeback_approval: "Freigabe",
  kpi: "KPI",
  verzug: "Verzug",
  wp: "Arbeitspaket",
  prozess: "Prozess",
  evidence: "Nachweis",
  profil: "Profil",
  decision: "Entscheidung",
  meeting: "Meeting",
  ai_ready: "AI-Ready-Check",
  kanban: "Kanban",
  formular: "Formular",
};
